import { select, drag } from 'd3';
import {
  OmniStyleElement,
  OmniElement,
  styleMap,
  classMap,
  html,
  css,
} from 'omni-ui';

OmniStyleElement.register();

/**
 * Class for `<campaign-draggable-chips>` element. This component presumes that the direct parent of this component is the boundary for drag and drop and will confine chips to the container.
 *
 * This component is **dumb** as far as the {@link #chips} themselves are concerned, it only manages the D3 drag and drop functionality and caluclations for positioning. Every update to those values is passed to the {@link #handleUpdateChip} handler.
 * @extends OmniElement
 */
export class CampaignDraggableChips extends OmniElement {
  /**
   * @property {object[]} chips - An array of chips to display
   * @property {string} chips.text - Text to display on the chip
   * @property {string} chips.top - A CSS percentage for the distance from the top of the container boundary (i.e. `10%`). This will be passed to the {@link #handleUpdateChip}
   * @property {string} chips.left - A CSS percentage for the distance from the left of the container boundary (i.e. `10%`). This will be passed to the {@link #handleUpdateChip}
   * @property {function} handleUpdateChip - A function that is called when an item is dragged. It will pass an `index` argument, as well as a `value` argument. The former represents the items index in the array, the latter is a hash object containing the keys `top` and `left` with their respective percentage values in a string format.
   * @property {function} handleRemoveChip - A function that is called when a chip's _close_ button is clicked. It will pass an `index` argument. If this property is not specified, no _close_ button will appear.
   * @property {Boolean} handleRemoveChip - A function that is called when a chip's _close_ button is clicked. It will pass an `index` argument. If this property is not specified, no _close_ button will appear.
   */
  static get properties() {
    return {
      chips: { type: Array },
      containerPadding: { type: Number },
      handleRaise: { attribute: false },
      handleUpdateChip: { attribute: false },
      handleRemoveChip: { attribute: false },
      readonly: { type: Boolean },
    };
  }

  /** Lit-Element styles */
  static get styles() {
    return [
      super.styles,
      css`
        .chip {
          position: absolute;
          background-color: rgb(var(--rgb-core-medium-two));
          cursor: grab;
          max-width: 50%;
          border-radius: 0.285rem;
          z-index: 2;
          user-select: none;
          transition:
            background-color 0.3s ease,
            box-shadow 0.3s ease;
          box-shadow: 0 0.05rem 0.05rem 0 rgba(0, 0, 0, 0.0125);
          display: flex;
          justify-content: stretch;
          align-items: stretch;
        }

        .chip.readonly {
          cursor: default;
          /* make up padding lost from chip in readonly mode */
          padding-right: 0.286rem;
        }

        .chip.is-dragging {
          box-shadow: 0 1rem 0.75rem 0 rgba(0, 0, 0, 0.05);
          background-color: rgb(var(--rgb-core-light));
          transition:
            background-color 0.3s ease,
            box-shadow 0.3s ease;
        }

        .chip.is-dragstart {
          opacity: 0;
        }

        .chip-text {
          margin: 0.286rem 0.4285rem 0.286rem 0.857rem;
          flex-grow: 1;
          word-break: break-word;
          hyphens: auto;
        }

        .omni button.chip-close {
          border: none;
          padding: 0;
          background: transparent;
          cursor: pointer;
          width: 2.142rem;
          height: 2.142rem;
          line-height: 2.142rem;
          flex-shrink: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .chip-icon {
          margin: 0;
        }
      `,
    ];
  }

  /** Init {@link #chips} property, and default {@link #containerPadding} property value */
  constructor() {
    super();

    /** properties */
    this.chips = [];
    this.containerPadding = 15;

    this.adjustPercent = 0;
    this.adjustLeftOffset = 0;
  }

  /** Call {@link #_initD3Listeners} after first render */
  firstUpdated() {
    this._initD3Listeners();
  }

  /**
   * Call {@link #_initD3Listeners} after updates to {@link #chips} so new chips are draggable
   * @todo: this won't work because chips changes every time we update the position
   */
  async updated(changedProps) {
    const changedChips = changedProps.get('chips');
    if (changedChips && changedChips.length !== this.chips.length) {
      this._adjustStartingPos(changedChips.length);
      this._initD3Listeners();
    }
  }

  /**
   * Offsets new chips for visabilty.
   * @param {Number} idx - Index of newly added chip
   */
  _adjustStartingPos(idx) {
    if (this.chips[idx] === undefined) return;

    if (idx === 0) {
      this.adjustPercent = 0;
    }

    const newTop = parseFloat(this.chips[idx].top) + this.adjustPercent;
    const newLeft =
      parseFloat(this.chips[idx].left) +
      this.adjustPercent +
      this.adjustLeftOffset;

    this.handleUpdateChip(idx, {
      top: `${newTop}%`,
      left: `${newLeft}%`,
    });

    if (newTop > 85) {
      this.adjustPercent = 0;
      this.adjustLeftOffset += 3;
    } else if (newLeft > 85) {
      this.adjustPercent = 0;
      this.adjustLeftOffset = 0;
    } else {
      this.adjustPercent += 3;
    }
  }

  /**
   * Initializes D3 drag and drop listeners on `this.shadowRoot`.
   * D3 handles removing listeners when a listener is applied to the same event name.
   * This attaches {@link #_handleDragStart} to the `start` event, and {@link #_handleDragEnd} to the `end` event. It assigns the _result_ of {@link #_getHandleDrag} to the `drag` event.
   * @see {@link #_getHandleDrag}
   */
  _initD3Listeners() {
    if (this.readonly) return;

    select(this.shadowRoot)
      .selectAll('.chip')
      .call(
        drag()
          .on('start', this._handleDragStart)
          .on('drag', this._getHandleDrag())
          .on('end', this._handleDragEnd)
          .filter(
            event =>
              !event.ctrlKey &&
              !event.button &&
              !event.target.closest('button'),
          ),
      );
  }

  _handleDragStart() {
    select(this)
      .classed('is-dragging', true)
      .attr('part', 'chip chip-dragging')
      .selectAll('[part]')
      .each(function eachChipPartAdd() {
        const el = select(this);
        el.attr('part', `${el.attr('part')} ${el.attr('part')}-dragging`);
      });
  }

  _getHandleDrag() {
    const that = this;

    return function _handleDrag({ x, y }) {
      // do check here to see if in-bounds
      const { width: parentWidth, height: parentHeight } =
        that.parentNode?.getBoundingClientRect() || {};
      const { width, height } = this.getBoundingClientRect();

      let top = ((y - height / 2) / parentHeight) * 100;
      let left = ((x - width / 2) / parentWidth) * 100;

      /** beyond top boundary */
      if (y <= height / 2 + that.containerPadding) {
        top = (that.containerPadding / parentHeight) * 100;
        /** beyond bottom boundary */
      } else if (y >= parentHeight - (height / 2 + that.containerPadding)) {
        top =
          ((parentHeight - (height + that.containerPadding)) / parentHeight) *
          100;
      }

      /** beyond left boundary */
      if (x <= that.containerPadding + width / 2) {
        left = (that.containerPadding / parentWidth) * 100;
        /** beyond right boundary */
      } else if (x >= parentWidth - (width / 2 + that.containerPadding)) {
        left =
          ((parentWidth - (width + that.containerPadding)) / parentWidth) * 100;
      }

      that.handleUpdateChip(this.dataset.index, {
        top: `${top}%`,
        left: `${left}%`,
      });
    };
  }

  _handleDragEnd() {
    select(this)
      .classed('is-dragging', false)
      .attr('part', 'chip')
      .selectAll('[part]')
      .each(function eachChipPartRemove() {
        const el = select(this);
        el.attr('part', el.attr('part')?.split(' ')[0]);
      });
  }

  _renderChip = ({ text, top, left, background, color }, ind) => html`
    <div
      part="chip"
      class=${classMap({
        chip: true,
        readonly: this.readonly,
      })}
      data-index=${ind}
      style=${styleMap({ top, left, background, color })}
    >
      <div part="chip-text" class="chip-text">${text}</div>
      ${this.handleRemoveChip && !this.readonly
        ? html`
            <button
              part="chip-close"
              class="chip-close"
              data-index=${ind}
              @click=${() => {
                this.handleRemoveChip(ind);
              }}
            >
              <omni-icon
                part="chip-icon"
                class="chip-icon is-size-3"
                icon-id="omni:interactive:remove"
                style=${styleMap({ fill: color })}
              ></omni-icon>
            </button>
          `
        : null}
    </div>
  `;

  render() {
    return html`
      <omni-style> ${this.chips.map(this._renderChip)} </omni-style>
    `;
  }
}

OmniElement.register('campaign-draggable-chips', CampaignDraggableChips);
